import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";
import { parseMoneyAmount, normalizeCampusKey } from "../../utils/feeCalculator";

export default function LegacyBalances() {
  const currentUser = getCurrentUser();
  const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin" || String(currentUser?.role || "").toLowerCase() === "superadmin";
  const [rows, setRows] = useState([]);
  const [campus, setCampus] = useState("");
  const [queryText, setQueryText] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({
    studentId: "",
    studentName: "",
    campus: "",
    schoolYear: "",
    paymentDate: "",
    receiptNo: "",
    amount: "",
    notes: ""
  });

  useEffect(() => {
    const q = query(collection(db, "legacy_balances"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRows(list);
    }, () => {
      setRows([]);
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = String(queryText || "").trim().toLowerCase();
    const c = normalizeCampusKey(campus);
    return (rows || []).filter(r => {
      const rc = normalizeCampusKey(r.campus);
      if (c && rc !== c) return false;
      if (!q) return true;
      const sid = String(r.studentId || "").toLowerCase();
      const sn = String(r.studentName || "").toLowerCase();
      const sy = String(r.schoolYear || "").toLowerCase();
      const pr = String(r.receiptNo || r.reference || "").toLowerCase();
      const pd = String(r.paymentDate || "").toLowerCase();
      return sid.includes(q) || sn.includes(q) || sy.includes(q) || pr.includes(q) || pd.includes(q);
    });
  }, [rows, campus, queryText]);

  const resetForm = () => {
    setEditingId("");
    setForm({ studentId: "", studentName: "", campus: "", schoolYear: "", paymentDate: "", receiptNo: "", amount: "", notes: "" });
  };

  const startEdit = (r) => {
    setEditingId(String(r.id));
    setForm({
      studentId: String(r.studentId || ""),
      studentName: String(r.studentName || ""),
      campus: String(r.campus || ""),
      schoolYear: String(r.schoolYear || ""),
      paymentDate: String(r.paymentDate || ""),
      receiptNo: String(r.receiptNo || r.reference || ""),
      amount: String(r.amount ?? ""),
      notes: String(r.notes || "")
    });
  };

  const save = async () => {
    const payload = {
      studentId: String(form.studentId || "").trim(),
      studentName: String(form.studentName || "").trim(),
      campus: normalizeCampusKey(form.campus),
      schoolYear: String(form.schoolYear || "").trim(),
      paymentDate: String(form.paymentDate || "").trim(),
      receiptNo: String(form.receiptNo || "").trim(),
      amount: parseMoneyAmount(form.amount),
      notes: String(form.notes || "").trim(),
      updatedAt: new Date().toISOString(),
      updatedBy: String(currentUser?.email || currentUser?.uid || "")
    };
    if (!payload.studentId && !payload.studentName) {
      alert("Please enter Student ID or Student Name.");
      return;
    }
    if (!payload.campus) {
      alert("Please select Campus.");
      return;
    }
    if (!Number.isFinite(payload.amount) || payload.amount <= 0) {
      alert("Please enter a valid Amount.");
      return;
    }
    if (!payload.schoolYear) payload.schoolYear = "Previous Years";

    try {
      if (editingId) {
        await updateDoc(doc(db, "legacy_balances", editingId), payload);
      } else {
        await addDoc(collection(db, "legacy_balances"), {
          ...payload,
          createdAt: new Date().toISOString(),
          createdBy: String(currentUser?.email || currentUser?.uid || "")
        });
      }
      resetForm();
    } catch (e) {
      alert("Failed to save. Please try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this legacy balance record?")) return;
    try {
      await deleteDoc(doc(db, "legacy_balances", String(id)));
    } catch {
      alert("Failed to delete.");
    }
  };

  const total = useMemo(() => filtered.reduce((s, r) => s + parseMoneyAmount(r.amount), 0), [filtered]);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Legacy Balances</h3>
          <div className="text-muted small">Record prior-year balances here. These do not affect current-year tuition computations.</div>
        </div>
        <div className="text-muted small">Total: ₱{Number(total || 0).toLocaleString()}</div>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-2">
            <label className="form-label">Student ID</label>
            <input className="form-control" value={form.studentId} onChange={e => setForm(f => ({ ...f, studentId: e.target.value }))} placeholder="e.g., S-GAPAN-0001" />
          </div>
          <div className="col-md-3">
            <label className="form-label">Student Name</label>
            <input className="form-control" value={form.studentName} onChange={e => setForm(f => ({ ...f, studentName: e.target.value }))} placeholder="e.g., Juan Dela Cruz" />
          </div>
          <div className="col-md-2">
            <label className="form-label">Campus</label>
            <select className="form-select" value={form.campus} onChange={e => setForm(f => ({ ...f, campus: e.target.value }))}>
              <option value="">Select</option>
              <option value="gapan">gapan</option>
              <option value="bulacan">bulacan</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">School Year</label>
            <input className="form-control" value={form.schoolYear} onChange={e => setForm(f => ({ ...f, schoolYear: e.target.value }))} placeholder="e.g., 2024-2025" />
          </div>
          <div className="col-md-1">
            <label className="form-label">Pay Date</label>
            <input className="form-control" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} placeholder="YYYY-MM-DD" />
          </div>
          <div className="col-md-1">
            <label className="form-label">Receipt #</label>
            <input className="form-control" value={form.receiptNo} onChange={e => setForm(f => ({ ...f, receiptNo: e.target.value }))} placeholder="OR" />
          </div>
          <div className="col-md-2">
            <label className="form-label">Amount</label>
            <input className="form-control" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g., 3500" />
          </div>
          <div className="col-md-1 d-grid gap-2">
            <button className="btn btn-primary" onClick={save}>{editingId ? "Update" : "Add"}</button>
            {editingId && <button className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>}
          </div>
          <div className="col-12">
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
          </div>
        </div>
      </div>

      <div className="card p-3">
        <div className="row g-2 align-items-end mb-2">
          <div className="col-md-3">
            <label className="form-label mb-0">Filter Campus</label>
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
              <option value="">All</option>
              <option value="gapan">gapan</option>
              <option value="bulacan">bulacan</option>
            </select>
          </div>
          <div className="col-md-5">
            <label className="form-label mb-0">Search</label>
            <input className="form-control" value={queryText} onChange={e => setQueryText(e.target.value)} placeholder="Student name, ID, school year, receipt #, or date" />
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Student</th>
                <th>Student ID</th>
                <th>Campus</th>
                <th>School Year</th>
                <th>Pay Date</th>
                <th>Receipt #</th>
                <th className="text-end">Amount</th>
                <th>Notes</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td>{r.studentName || "-"}</td>
                  <td>{r.studentId || "-"}</td>
                  <td>{r.campus || "-"}</td>
                  <td>{r.schoolYear || "-"}</td>
                  <td>{r.paymentDate || "-"}</td>
                  <td>{r.receiptNo || r.reference || "-"}</td>
                  <td className="text-end">₱{Number(parseMoneyAmount(r.amount)).toLocaleString()}</td>
                  <td>{r.notes || ""}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => startEdit(r)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(r.id)} disabled={!isAdmin && String(currentUser?.role || "").toLowerCase() !== "finance"}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No records.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
